import { useEffect, useState } from "react";
import { Button, DatePicker, Form, Input, Modal, Space, Switch, Table, Tag, Upload, message } from "antd";
import { HistoryOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import moment from "moment";
import NetworkApi from "../../network/networkApi";
import config from "../../config";

const empty = { title: "", content: "", organizationName: "", startDate: null, endDate: null, isVisible: true, showOnHome: true };
const asAttachmentList = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.fileList)) return value.fileList;
  return [];
};
const getAttachments = (row: any) => {
  const attachments = asAttachmentList(row.attachments);
  return attachments.length > 0
    ? attachments
    : row.attachmentName
      ? [{ name: row.attachmentName, originalName: row.attachmentOriginalName || row.attachmentName }]
      : [];
};

const AnnouncementList = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [form] = Form.useForm();

  const load = async () => {
    const response = await NetworkApi.requestGet("/api/admin/announcement/list");
    if (response.resultCode === 200) setRows(Array.isArray(response.result) ? response.result : []);
    else message.error(response.message || "Мэдэгдэл татахад алдаа гарлаа");
  };
  useEffect(() => { load(); }, []);

  const open = (row?: any) => {
    setEditing(row || { ...empty });
    form.setFieldsValue(row ? { ...row, startDate: moment(row.startDate), endDate: moment(row.endDate) } : empty);
  };
  const save = async (values: any) => {
    const fileList = asAttachmentList(values.attachment);
  const files = fileList.map((item: any) => item.originFileObj || item).filter(Boolean);
  const invalidFile = files.find((file: File) => !file.name.toLowerCase().endsWith(".pdf"));
  if (invalidFile) return message.error("Зөвхөн PDF файл хавсаргана уу");
    let uploaded: any = {};
    if (files.length > 0) {
      const responses: any[] = await Promise.all(files.map((file: File) => NetworkApi.requestUploadFile(file)));
      const failed = responses.find((response) => response.resultCode !== 200);
      if (failed) return message.error(failed.message || "Файл хуулахад алдаа гарлаа");
      uploaded = { attachments: responses.map((response, index) => ({ name: response.result.name, originalName: files[index].name, mimetype: files[index].type })) };
    }
    const { attachment, ...formValues } = values;
    const body = { ...formValues, startDate: values.startDate.toISOString(), endDate: values.endDate.toISOString(), ...uploaded };
    const response = editing?.id ? await NetworkApi.requestPut(`/api/admin/announcement/${editing.id}`, body) : await NetworkApi.requestPost("/api/admin/announcement", body);
    if (response.resultCode !== 200) return message.error(response.message || "Хадгалахад алдаа гарлаа");
    message.success("Мэдэгдэл хадгалагдлаа"); setEditing(null); form.resetFields(); load();
  };
  const showHistory = async (id: number) => {
    const response = await NetworkApi.requestGet(`/api/admin/announcement/${id}/history`);
    if (response.resultCode === 200) setHistory(Array.isArray(response.result) ? response.result : []);
    else message.error(response.message || "Түүх татахад алдаа гарлаа");
  };
  const columns = [
    { title: "Гарчиг", dataIndex: "title" },
    { title: "Оруулсан огноо", dataIndex: "createdAt", render: (v: string) => moment(v).format("YYYY-MM-DD HH:mm") },
    { title: "Байгууллага", dataIndex: "organizationName" },
    { title: "Эхлэх", dataIndex: "startDate", render: (v: string) => moment(v).format("YYYY-MM-DD HH:mm") },
    { title: "Дуусах", dataIndex: "endDate", render: (v: string) => moment(v).format("YYYY-MM-DD HH:mm") },
    { title: "Төлөв", dataIndex: "status", render: (v: string, r: any) => <Tag color={v === "active" ? "green" : "default"}>{v === "active" ? "Идэвхтэй" : "Идэвхгүй"}</Tag> },
    { title: "Нүүр хуудас", dataIndex: "showOnHome", render: (v: boolean) => <Tag color={v ? "blue" : "default"}>{v ? "Харуулна" : "Харуулахгүй"}</Tag> },
    { title: "Хавсралтууд", render: (_: any, r: any) => <div>{getAttachments(r).map((file: any) => <div key={file.name}><a href={`${config.get("apiUrl")}/tmp/${file.name}`} target="_blank" rel="noreferrer">{file.originalName || file.name}</a></div>)}</div> },
    { title: "Оруулсан", dataIndex: "createdByName", render: (_: any, r: any) => <span>{r.createdByName}<br />{r.createdByPosition}</span> },
    { title: "Үйлдэл", render: (_: any, r: any) => <Space><Button onClick={() => open(r)}>Засах</Button><Button icon={<HistoryOutlined />} onClick={() => showHistory(r.id)}>Түүх</Button></Space> },
  ];
  return <div style={{ padding: 24 }}>
    <Space style={{ marginBottom: 16 }}><h2 style={{ margin: 0 }}>Мэдэгдэл</h2><Button type="primary" icon={<PlusOutlined />} onClick={() => open()}>Нэмэх</Button></Space>
    <Table rowKey="id" columns={columns} dataSource={rows} />
    <Modal title={editing?.id ? "Мэдэгдэл засах" : "Мэдэгдэл нэмэх"} open={!!editing} onCancel={() => setEditing(null)} onOk={() => form.submit()} width={700} destroyOnClose>
      <Form form={form} layout="vertical" initialValues={empty} onFinish={save}>
        <Form.Item name="title" label="Гарчиг" rules={[{ required: true, message: "Гарчиг оруулна уу" }]}><Input /></Form.Item>
        <Form.Item name="organizationName" label="Байгууллагын нэр" rules={[{ required: true, message: "Байгууллагын нэр оруулна уу" }]}><Input /></Form.Item>
        <Space style={{ display: "flex" }} align="start"><Form.Item name="startDate" label="Эхлэх хугацаа" rules={[{ required: true, message: "Эхлэх хугацаа сонгоно уу" }]}><DatePicker showTime format="YYYY-MM-DD HH:mm" /></Form.Item><Form.Item name="endDate" label="Дуусах хугацаа" rules={[{ required: true, message: "Дуусах хугацаа сонгоно уу" }]}><DatePicker showTime format="YYYY-MM-DD HH:mm" /></Form.Item></Space>
        <Form.Item name="content" label="Мэдэгдлийн агуулга" rules={[{ required: true, message: "Агуулга оруулна уу" }]}><Input.TextArea rows={6} /></Form.Item>
        <Form.Item name="isVisible" label="Харуулах" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="showOnHome" label="Нүүр хуудсанд харуулах" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="attachment" label="Файл хавсаргах" valuePropName="fileList" getValueFromEvent={(event: any) => event?.fileList || []}><Upload beforeUpload={() => false} maxCount={5} multiple accept=".pdf,application/pdf"><Button icon={<UploadOutlined />}>PDF файл сонгох</Button></Upload></Form.Item>
        {getAttachments(editing || {}).length > 0 && <div><strong>Одоогийн хавсралтууд:</strong>{getAttachments(editing || {}).map((file: any) => <div key={file.name}><a href={`${config.get("apiUrl")}/tmp/${file.name}`} target="_blank" rel="noreferrer">{file.originalName || file.name}</a></div>)}</div>}
      </Form>
    </Modal>
    <Modal title="Зассан түүх" open={history.length > 0} onCancel={() => setHistory([])} footer={null} width={800}>
      <Table rowKey="id" dataSource={history} pagination={false} columns={[{ title: "Үйлдэл", dataIndex: "action" }, { title: "Огноо", dataIndex: "createdAt", render: (v: string) => moment(v).format("YYYY-MM-DD HH:mm") }, { title: "Мэргэжилтэн", dataIndex: "changedByName" }, { title: "Албан тушаал", dataIndex: "changedByPosition" }, { title: "Төлөв", dataIndex: "isVisible", render: (v: boolean) => v ? "Харуулна" : "Нуусан" }]} />
    </Modal>
  </div>;
};
export default AnnouncementList;
